import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MailIcon,
  LockClosedIcon,
  EyeIcon,
  EyeOffIcon,
  OfficeBuildingIcon,
  LoginIcon,
  ShieldCheckIcon,
  CodeIcon,
} from "@heroicons/react/outline";

const Login = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const bookingWidgetDocsUrl = process.env.REACT_APP_BOOKING_WIDGET_DOCS_URL || `${(baseUrl || "").replace(/\/api\/v1\.0\/?$/, "")}/docs/booking-widget`;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [loginDisabled, setLoginDisabled] = useState(true);

  const handleTogglePasswordVisibility = () => {
    setPasswordVisible((prevVisible) => !prevVisible);
  };

  const isValidEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleEmailChange = (e) => {
    const enteredEmail = e.target.value;
    setEmail(enteredEmail);
    setEmailTouched(true);

    if (enteredEmail.trim() === "") {
      setEmailError("Email is required");
    } else if (!isValidEmail(enteredEmail)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
    toggleLoginButton(enteredEmail, password);
  };

  const handlePasswordChange = (e) => {
    const enteredPassword = e.target.value;
    setPassword(enteredPassword);
    setPasswordTouched(true);

    setPasswordError(
      enteredPassword.trim() === "" ? "Password is required" : ""
    );

    toggleLoginButton(email, enteredPassword);
  };

  const toggleLoginButton = (email, password) => {
    setLoginDisabled(email.trim() === "" || password.trim() === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${baseUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Response Data:", data);

        const accessToken = data.data.authorization.token;
        localStorage.setItem("accessToken", accessToken);

        // Store user object for role-based view
        let userRole = null;
        if (data.data && data.data.user) {
          localStorage.setItem("user", JSON.stringify(data.data.user));
          window.dispatchEvent(new Event("storageChanged"));
          userRole = data.data.user.role?.role_name || null;
        }

        toast.success("Login successful");
        const resortIdFromResponse =
          data.data.user?.resort_id || data.data.user?.resortId || null;
        const isPropertyUser = ["resort-supervisor", "resort-manager"].includes(userRole);

        // Property users should land in their tenant context immediately. The
        // route guard and API authorization remain authoritative if a user
        // tampers with the URL after login.
        if (isPropertyUser && resortIdFromResponse) {
          navigate(`/dashboard/view-property/${encodeURIComponent(resortIdFromResponse)}`);
        } else if (isPropertyUser) {
          // An unassigned property user cannot safely be sent to a property.
          navigate("/dashboard/property");
        } else {
          navigate("/dashboard");
        }
      } else {
        toast.error("Login failed! Check login details");
        console.error("Login failed");
      }
    } catch (error) {
      toast.error("Error occurred while logging in");
      console.error("Error occurred while logging in:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Animated Background */}
      <div className="login-background">
        <div className="floating-orb orb-1"></div>
        <div className="floating-orb orb-2"></div>
        <div className="floating-orb orb-3"></div>
      </div>

      <div className="login-content">
        {/* Left Side - Branding */}
        <div className="login-left">
          <div className="brand-section">
            <div className="brand-logo">
              <OfficeBuildingIcon className="brand-icon" />
            </div>
            <h1 className="brand-title">
              <span className="brand-highlight">Channel</span> Manager
            </h1>
            <p className="brand-subtitle">
              Manage all your properties efficiently in one unified platform
            </p>

            {/* Features List */}
            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">
                  <ShieldCheckIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="feature-title">Secure & Reliable</h3>
                  <p className="feature-desc">
                    Enterprise-grade security for your data
                  </p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <OfficeBuildingIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="feature-title">Multi-Property Management</h3>
                  <p className="feature-desc">
                    Handle multiple properties seamlessly
                  </p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="feature-title">Lightning Fast</h3>
                  <p className="feature-desc">
                    Real-time updates and instant access
                  </p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <CodeIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="feature-title">
                    <a
                      href={bookingWidgetDocsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="feature-link"
                    >
                      Booking Widget
                    </a>
                  </h3>
                  <p className="feature-desc">
                    Embed branded, secure reservations on any website ·{" "}
                    <a
                      href={bookingWidgetDocsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="feature-link feature-docs-link"
                    >
                      View implementation docs
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Illustration */}
            <div className="illustration">
              <svg viewBox="0 0 200 200" className="w-full h-auto">
                <defs>
                  <linearGradient
                    id="grad1"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: "#8b5cf6", stopOpacity: 1 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: "#6366f1", stopOpacity: 1 }}
                    />
                  </linearGradient>
                </defs>
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="url(#grad1)"
                  opacity="0.1"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="60"
                  fill="url(#grad1)"
                  opacity="0.2"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="40"
                  fill="url(#grad1)"
                  opacity="0.3"
                />
                <path
                  d="M100 60 L120 80 L100 140 L80 80 Z"
                  fill="url(#grad1)"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-right">
          <div className="login-form-container">
            {/* Header */}
            <div className="form-header">
              <p className="form-kicker">SECURE WORKSPACE</p>
              <h2 className="form-title">Welcome back</h2>
              <p className="form-subtitle">Sign in to manage your properties</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="login-form">
              {/* Email Input */}
              <div className="form-group">
                <label className="form-label">
                  <MailIcon className="label-icon" />
                  Email Address
                </label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email"
                    className={`form-input ${
                      emailTouched && emailError ? "input-error" : ""
                    }`}
                  />
                  <MailIcon className="input-icon" />
                </div>
                {emailError && emailTouched && (
                  <p className="error-message">{emailError}</p>
                )}
              </div>

              {/* Password Input */}
              <div className="form-group">
                <label className="form-label">
                  <LockClosedIcon className="label-icon" />
                  Password
                </label>
                <div className="input-wrapper">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Enter your password"
                    className={`form-input ${
                      passwordTouched && passwordError ? "input-error" : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleTogglePasswordVisibility}
                    className="password-toggle"
                  >
                    {passwordVisible ? (
                      <EyeOffIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {passwordError && passwordTouched && (
                  <p className="error-message">{passwordError}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="form-options">
                <label className="remember-me">
                  <input type="checkbox" className="checkbox-input" />
                  <span className="checkbox-label">Remember me</span>
                </label>
                <Link to="/forgot-password" className="forgot-link">
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loginDisabled || loading}
                className="submit-button"
              >
                {loading ? (
                  <>
                    <svg className="spinner" viewBox="0 0 24 24">
                      <circle
                        className="spinner-circle"
                        cx="12"
                        cy="12"
                        r="10"
                        fill="none"
                        strokeWidth="4"
                      />
                      <path
                        className="spinner-path"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <LoginIcon className="w-5 h-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>

              {/* Sign Up Link */}
              {/* <div className="signup-prompt">
                <p>
                  Don't have an account?{" "}
                  <Link to="/register" className="signup-link">
                    Create account
                  </Link>
                </p>
              </div> */}
            </form>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .login-container {
          min-height: 100svh;
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at 12% 18%, rgba(79, 70, 229, 0.24), transparent 34%),
            radial-gradient(circle at 86% 82%, rgba(14, 165, 233, 0.2), transparent 34%),
            #0b1020;
        }

        .login-background {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          background-image: linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
          background-size: 56px 56px;
          mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.8), transparent 85%);
        }

        .floating-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          animation: float 20s ease-in-out infinite;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: rgba(99, 102, 241, 0.34);
          top: -100px;
          left: -100px;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 500px;
          height: 500px;
          background: rgba(14, 165, 233, 0.24);
          bottom: -150px;
          right: -150px;
          animation-delay: 7s;
        }

        .orb-3 {
          width: 350px;
          height: 350px;
          background: rgba(129, 140, 248, 0.16);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: 14s;
        }

        @keyframes float {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(50px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-50px, 50px) scale(0.9);
          }
        }

        .login-content {
          position: relative;
          z-index: 1;
          min-height: 100svh;
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(400px, 0.95fr);
          align-items: center;
          max-width: 1440px;
          margin: 0 auto;
          padding: clamp(1.25rem, 3vw, 3rem);
          gap: clamp(2rem, 6vw, 6rem);
        }

        .login-left {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 0;
          padding: 1rem;
        }

        .brand-section {
          width: 100%;
          max-width: 560px;
        }

        .brand-logo {
          width: 68px;
          height: 68px;
          background: linear-gradient(145deg, rgba(129, 140, 248, 0.95), rgba(37, 99, 235, 0.8));
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.75rem;
          box-shadow: 0 18px 38px -12px rgba(37, 99, 235, 0.65);
        }

        .brand-icon {
          width: 40px;
          height: 40px;
          color: white;
        }

        .brand-title {
          font-size: clamp(2.4rem, 5vw, 4rem);
          font-weight: 800;
          color: white;
          letter-spacing: -0.04em;
          margin-bottom: 0.85rem;
          line-height: 1.2;
        }

        .brand-highlight {
          background: linear-gradient(to right, #fbbf24, #f59e0b);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .brand-subtitle {
          max-width: 32rem;
          font-size: 1.05rem;
          color: rgba(226, 232, 240, 0.82);
          margin-bottom: 2.5rem;
          line-height: 1.65;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          padding: 0.75rem 0.85rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.045);
          backdrop-filter: blur(10px);
        }

        .feature-icon {
          width: 40px;
          height: 40px;
          background: rgba(129, 140, 248, 0.22);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .feature-title {
          font-size: 1rem;
          font-weight: 600;
          color: #f8fafc;
          margin-bottom: 0.25rem;
        }

        .feature-desc {
          font-size: 0.875rem;
          color: rgba(203, 213, 225, 0.72);
        }

        .feature-link {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s ease, opacity 0.2s ease;
        }

        .feature-link:hover,
        .feature-link:focus-visible {
          color: #fbbf24;
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .feature-docs-link {
          color: #fbbf24;
          font-weight: 600;
          white-space: nowrap;
        }

        .illustration {
          max-width: 220px;
          margin: 1rem 0 0 auto;
          opacity: 0.22;
          animation: pulse 4s ease-in-out infinite;
        }

        .login-right {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 0;
          padding: 1rem;
        }

        .login-form-container {
          width: 100%;
          max-width: 460px;
          position: relative;
          overflow: hidden;
          background: rgba(255, 255, 255, 0.97);
          border: 1px solid rgba(255, 255, 255, 0.8);
          border-radius: 26px;
          padding: clamp(2rem, 4vw, 3rem);
          box-shadow: 0 32px 80px -26px rgba(2, 6, 23, 0.78), 0 10px 26px -12px rgba(2, 6, 23, 0.35);
        }

        .login-form-container::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 4px;
          background: linear-gradient(90deg, #6366f1, #38bdf8);
        }

        .form-header {
          text-align: center;
          margin-bottom: 2.25rem;
        }

        .form-kicker {
          color: #6366f1;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.2em;
          margin-bottom: 0.65rem;
        }

        .form-title {
          font-size: clamp(1.75rem, 3vw, 2.15rem);
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.035em;
          margin-bottom: 0.5rem;
        }

        .form-subtitle {
          font-size: 1rem;
          color: #64748b;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .label-icon {
          width: 1rem;
          height: 1rem;
          color: #8b5cf6;
        }

        .input-wrapper {
          position: relative;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          padding-right: 3rem;
          border: 1px solid #dbe3ef;
          border-radius: 12px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f8fafc;
        }

        .form-input:focus {
          outline: none;
          border-color: #6366f1;
          background: white;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
        }

        .input-error {
          border-color: #ef4444;
        }

        .input-error:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .input-icon {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
          pointer-events: none;
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 0.375rem;
          transition: all 0.2s ease;
        }

        .password-toggle:hover {
          color: #8b5cf6;
          background: #f3f4f6;
        }

        .error-message {
          font-size: 0.75rem;
          color: #ef4444;
          margin-top: 0.25rem;
        }

        .form-options {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .remember-me {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .checkbox-input {
          width: 1rem;
          height: 1rem;
          border-radius: 0.25rem;
          border: 2px solid #d1d5db;
          cursor: pointer;
        }

        .checkbox-input:checked {
          background: #8b5cf6;
          border-color: #8b5cf6;
        }

        .checkbox-label {
          font-size: 0.875rem;
          color: #374151;
        }

        .forgot-link {
          font-size: 0.875rem;
          color: #8b5cf6;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .forgot-link:hover {
          color: #7c3aed;
        }

        .submit-button {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #4f46e5, #2563eb);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 10px 20px -10px rgba(37, 99, 235, 0.85);
        }

        .submit-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #4338ca, #1d4ed8);
          transform: translateY(-2px);
          box-shadow: 0 16px 26px -10px rgba(37, 99, 235, 0.9);
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 1.25rem;
          height: 1.25rem;
          animation: spin 1s linear infinite;
        }

        .spinner-circle {
          stroke: currentColor;
          opacity: 0.25;
        }

        .spinner-path {
          opacity: 0.75;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .signup-prompt {
          text-align: center;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .signup-link {
          color: #8b5cf6;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .signup-link:hover {
          color: #7c3aed;
        }

        @media (max-width: 1024px) {
          .login-content {
            grid-template-columns: 1fr;
          }

          .login-left {
            display: none;
          }

          .login-form-container {
            padding: 2rem;
          }
        }

        @media (max-width: 640px) {
          .login-content {
            padding: 1rem;
          }

          .login-form-container {
            padding: 1.5rem;
          }

          .form-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
